import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grid, Alert, Typography, FormControlLabel, Checkbox } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import IpdTextField from '../../../ipd/utils/IpdTextField';
import IpdFormField from '../../../ipd/utils/IpdFormField';
import WoLineItemsEditor from '../../components/material/WoLineItemsEditor';
import RfqVendorSelector from '../../components/material/RfqVendorSelector';
import RfqAttachmentsPanel from '../../components/material/RfqAttachmentsPanel';
import { createAndPublishWorkOrder, getWorkOrders } from '../../api/workOrder';
import { summarizeWoInviteNotify } from '../../utils/woInviteNotify';
import { getPurchasableVendors } from '../../api/vendor';
import { getRfq, getRfqItems, getRfqQuotes } from '../../api/rfq';
import {
  emptyWoLine, toWoCreatePayload, validateWoForm, nextWoNumber, linesFromRfq,
} from '../../utils/woEntity';
import { WO_MODES, WO_TYPE_SUGGESTIONS } from '../../constants/workOrder';
import { fromApiRfq, fromApiRfqItem, normalizeApiList } from '../../utils/rfqEntity';
import { IPD_COLORS } from '../../theme';
import useItemNames from '../../hooks/useItemNames';

const FORM_ID = 'wo-form';

function NativeDateField({ label, value, onChange, type = 'date' }) {
  return (
    <IpdFormField label={label}>
      <IpdTextField
        type={type}
        value={value || ''}
        onChange={onChange}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
    </IpdFormField>
  );
}

function defaultValues(mode) {
  return {
    mode: mode || 'DIRECT',
    wo_number: nextWoNumber([]),
    wo_date: dayjs().format('YYYY-MM-DD'),
    title: '',
    wo_type: '',
    priority: 'Medium',
    department: '',
    location: '',
    asset: '',
    description: '',
    direct_reason: '',
    rfq_id: '',
    rfq_number: '',
    quotation_number: '',
    other_reference: '',
    vendor_id: '',
    vendor_name: '',
    is_open_bid: true,
    submission_deadline: dayjs().add(7, 'day').hour(18).minute(0).format('YYYY-MM-DDTHH:mm'),
    planned_start: '',
    expected_completion: '',
    assigned_to: '',
    terms_and_conditions: '',
    notes: '',
  };
}

export default function WoForm() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const preselectedVendorId = query.get('vendor_id') || '';
  const prefillRfqId = query.get('rfq_id') || '';
  const listUrl = url.replace('/work-order/new', '/work-orders');
  const detailBase = url.replace('/work-order/new', '/work-orders');
  const { getPurchaseUomCode, getBaseUomCode, getPurchaseUomId, getGstRate } = useItemNames();

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [vendorError, setVendorError] = useState(null);
  const [lines, setLines] = useState([emptyWoLine()]);
  const [vendorIds, setVendorIds] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const submitModeRef = useRef('draft');

  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: defaultValues(query.get('mode') === 'BID' ? 'BID' : 'DIRECT'),
  });

  const mode = watch('mode');

  useEffect(() => {
    getPurchasableVendors()
      .then((list) => {
        setVendors(list);
        if (preselectedVendorId && list.some((v) => (v.vendor_id || v.id) === preselectedVendorId)) {
          setValue('vendor_id', preselectedVendorId);
          const hit = list.find((v) => (v.vendor_id || v.id) === preselectedVendorId);
          setValue('vendor_name', hit?.vendor_name || '');
        }
      })
      .catch(() => setVendors([]));
  }, [preselectedVendorId, setValue]);

  useEffect(() => {
    getWorkOrders()
      .then((data) => {
        const list = Array.isArray(data) ? data : (data?.items || data?.data || []);
        setValue('wo_number', nextWoNumber(list));
      })
      .catch(() => setValue('wo_number', nextWoNumber([])));
  }, [setValue]);

  useEffect(() => {
    if (!prefillRfqId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [rfqRaw, itemsRaw, quotesRaw] = await Promise.all([
          getRfq(prefillRfqId),
          getRfqItems(prefillRfqId).catch(() => []),
          getRfqQuotes(prefillRfqId).catch(() => []),
        ]);
        if (cancelled) return;
        const rfq = fromApiRfq(rfqRaw);
        const items = normalizeApiList(itemsRaw).map(fromApiRfqItem);
        const quotes = normalizeApiList(quotesRaw);
        setValue('rfq_id', rfq.id || prefillRfqId);
        setValue('rfq_number', rfq.rfq_number || '');
        setValue('title', rfq.rfq_title || rfq.title || '');
        setValue('description', rfq.notes || rfq.terms_and_conditions || rfq.rfq_title || '');
        setValue('department', rfq.department_name || '');
        setValue('terms_and_conditions', rfq.terms_and_conditions || '');
        const accepted = quotes.find((q) => String(q.status || '').toUpperCase() === 'ACCEPTED') || quotes[0];
        if (accepted) {
          setValue('quotation_number', accepted.quote_number || accepted.id || '');
          const vid = accepted.vendor_id;
          if (vid) {
            setValue('vendor_id', vid);
            setValue('mode', 'DIRECT');
          }
        }
        const mapped = linesFromRfq(items, accepted ? [accepted] : quotes);
        if (mapped.length) setLines(mapped);
      } catch (e) {
        toast.error(e.message || 'Could not load the RFQ');
      }
    })();
    return () => { cancelled = true; };
  }, [prefillRfqId, setValue]);

  const vendorOptions = vendors.map((v) => ({
    value: v.vendor_id || v.id,
    label: v.vendor_name || v.vendor_code || v.vendor_id,
  }));

  async function onSubmit(data) {
    const invitedVendors = vendors
      .filter((v) => vendorIds.includes(v.vendor_id || v.id))
      .map((v) => ({ vendor_id: v.vendor_id || v.id, vendor_name: v.vendor_name || '' }));
    const validationMsg = validateWoForm(data, lines, vendorIds);
    if (validationMsg) {
      setVendorError(data.mode === 'BID' && /invite/i.test(validationMsg) ? validationMsg : null);
      setSaveError(validationMsg);
      toast.error(validationMsg);
      return;
    }
    const vendor = vendors.find((v) => (v.vendor_id || v.id) === data.vendor_id);
    const payload = toWoCreatePayload(
      { ...data, vendor_name: vendor?.vendor_name || data.vendor_name },
      { lines, vendorIds, attachments, invitedVendors },
    );
    const publish = submitModeRef.current === 'publish';
    const issue = submitModeRef.current === 'issue';
    submitModeRef.current = 'draft';
    setSaving(true);
    setSaveError(null);
    try {
      const result = await createAndPublishWorkOrder(payload, { publish, issue });
      toast.success(
        publish
          ? `Work bid published. ${summarizeWoInviteNotify(result.notify)}`
          : issue ? 'Work Order issued to vendor.' : 'Saved as draft.',
      );
      const id = result?.id || result?.work_order_id;
      history.push(id ? `${detailBase}/${id}` : listUrl);
    } catch (e) {
      const msg = e.message || 'Failed to save work order';
      setSaveError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ pb: 10 }}>
      <PageHeader
        eyebrow="Material Management"
        title="New Work Order"
        description="Direct issue to a vendor, or publish a bid so vendors can quote."
        actions={(
          <InvButton variant="outlined" tone="teal" startIcon={<ArrowBackIcon />}
            onClick={() => history.push(listUrl)}>
            Exit
          </InvButton>
        )}
      />

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>{saveError}</Alert>
      )}

      <Box component="form" id={FORM_ID} noValidate onSubmit={handleSubmit(onSubmit)}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <SectionCard title="How is the vendor chosen?">
          <Controller name="mode" control={control}
            render={({ field }) => (
              <InvSelect label="Mode *" value={field.value} onChange={field.onChange} options={WO_MODES} />
            )} />
        </SectionCard>

        <SectionCard title="Work details">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Controller name="wo_number" control={control}
                render={({ field }) => <InvTextField {...field} label="Work Order Number" disabled />} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Controller name="wo_date" control={control}
                render={({ field }) => (
                  <NativeDateField label="Date" value={field.value} onChange={field.onChange} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Controller name="priority" control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Priority" placeholder="Low, Medium, High, Critical…" />
                )} />
            </Grid>
            <Grid item xs={12}>
              <Controller name="title" control={control} rules={{ required: 'Required' }}
                render={({ field }) => (
                  <InvTextField {...field} label="Work title *" error={errors.title} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="wo_type" control={control}
                render={({ field }) => (
                  <InvTextField
                    {...field}
                    label="Work type"
                    placeholder={`${WO_TYPE_SUGGESTIONS.slice(0, 4).join(', ')}…`}
                  />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="department" control={control}
                render={({ field }) => <InvTextField {...field} label="Department" />} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="location" control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Location" placeholder="Building, floor, room…" />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="asset" control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Asset / equipment" placeholder="Name, id or serial" />
                )} />
            </Grid>
            <Grid item xs={12}>
              <Controller name="description" control={control} rules={{ required: 'Required' }}
                render={({ field }) => (
                  <InvTextField {...field} label="Description / scope *" multiline rows={3} error={errors.description} />
                )} />
            </Grid>
            {mode === 'DIRECT' && (
              <Grid item xs={12}>
                <Controller name="direct_reason" control={control}
                  render={({ field }) => (
                    <InvTextField {...field} label="Reason for Direct Work Order *"
                      placeholder="Emergency repair, AMC visit, known contractor…" />
                  )} />
              </Grid>
            )}
          </Grid>
        </SectionCard>

        <WoLineItemsEditor
          lines={lines}
          onChange={setLines}
          showRates={mode === 'DIRECT'}
          getPurchaseUomCode={getPurchaseUomCode}
          getBaseUomCode={getBaseUomCode}
          getPurchaseUomId={getPurchaseUomId}
          getGstRate={getGstRate}
        />

        {mode === 'DIRECT' ? (
          <SectionCard title="Vendor">
            <Controller name="vendor_id" control={control}
              render={({ field }) => (
                <InvSelect
                  label="Vendor *"
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e);
                    const hit = vendors.find((v) => (v.vendor_id || v.id) === e.target.value);
                    setValue('vendor_name', hit?.vendor_name || '');
                  }}
                  options={vendorOptions}
                />
              )} />
          </SectionCard>
        ) : (
          <>
            <SectionCard title="Bidding">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Controller name="submission_deadline" control={control}
                    render={({ field }) => (
                      <NativeDateField
                        label="Quote deadline *"
                        type="datetime-local"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller name="is_open_bid" control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={(
                          <Checkbox
                            checked={Boolean(field.value)}
                            onChange={(e) => field.onChange(e.target.checked)}
                            sx={{ color: IPD_COLORS.primary }}
                          />
                        )}
                        label={(
                          <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                              Open bid — anyone with the URL can quote
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                              Invited vendors still get the link. Leave off for invite-only.
                            </Typography>
                          </Box>
                        )}
                      />
                    )} />
                </Grid>
              </Grid>
            </SectionCard>
            <RfqVendorSelector
              selectedIds={vendorIds}
              onChange={setVendorIds}
              preselectedId={preselectedVendorId}
              error={vendorError}
            />
          </>
        )}

        <SectionCard title="Schedule, terms, files">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Controller name="planned_start" control={control}
                render={({ field }) => (
                  <NativeDateField label="Planned start" value={field.value} onChange={field.onChange} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="expected_completion" control={control}
                render={({ field }) => (
                  <NativeDateField label="Expected completion" value={field.value} onChange={field.onChange} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="assigned_to" control={control}
                render={({ field }) => <InvTextField {...field} label="Assigned to" />} />
            </Grid>
            <Grid item xs={12}>
              <Controller name="terms_and_conditions" control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Terms & conditions" multiline rows={3} />
                )} />
            </Grid>
            <Grid item xs={12}>
              <Controller name="notes" control={control}
                render={({ field }) => <InvTextField {...field} label="Internal notes" multiline rows={2} />} />
            </Grid>
          </Grid>
        </SectionCard>

        <RfqAttachmentsPanel attachments={attachments} onChange={setAttachments} />

        <Box sx={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10,
          borderTop: `1px solid ${IPD_COLORS.border}`,
          backgroundColor: 'rgba(255,255,255,0.96)',
          px: { xs: 2, sm: 3 }, py: 1.5,
        }}>
          <Box sx={{ maxWidth: 1400, mx: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <InvButton type="submit" variant="outlined" tone="teal" loading={saving}
              onClick={() => { submitModeRef.current = 'draft'; }}>
              Save as Draft
            </InvButton>
            {mode === 'DIRECT' ? (
              <InvButton type="submit" tone="mint" disabled={saving}
                onClick={() => { submitModeRef.current = 'issue'; }}>
                Issue to vendor
              </InvButton>
            ) : (
              <InvButton type="submit" tone="mint" disabled={saving}
                onClick={() => { submitModeRef.current = 'publish'; }}>
                Publish bid
              </InvButton>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
